// The purchase funnel (LIS-1110) in the app: one fan's way through one
// purchase, from the access's Details to the purchase sheet closing, as Link
// in Bio records it (apps/link-in-bio/src/analytics/purchase-funnel.tsx).
// The Details screen mints a funnel ID for each access it shows and records
// every step under it; the checkout started from it carries the same ID
// (`funnelId`), which the server keeps on the checkout row, so each step
// joins how the purchase ended.

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Events, PurchaseFunnelProps } from "@libs/rudderstack/events";
import type { Purchase } from "@libs/platform-wire/purchase";
import { randomUuid } from "@libs/shared-ui";
import type { PurchaseFunnelStep } from "@libs/shared-ui";
import { track } from "@utils/rudderstack";
import { useAccessPurchase } from "./vendor/accessPurchase";

/** An access as the app reads it, from a list row or its access read. */
type Access = import("@hooks/trpc/useAccesses").Access;

/** The funnel the Details screen is in: its ID, and how a step is
 *  recorded. */
export type PurchaseFunnel = {
  id: string;
  step: (step: PurchaseFunnelStep) => void;
};

const PurchaseFunnelContext = createContext<PurchaseFunnel | null>(null);

export const PurchaseFunnelProvider = PurchaseFunnelContext.Provider;

/** The funnel of the Details screen this is in (its sheets too); null
 *  outside one. */
export function usePurchaseFunnel(): PurchaseFunnel | null {
  return useContext(PurchaseFunnelContext);
}

/** An event with an external link may be a vendor's (bought on the fan's
 *  behalf) or sold only through the link; a merch product is its vendor's.
 *  Either is known once the server has said whether it lists it. */
const listedOf = (access: Access) =>
  access.type === "merch" ||
  (access.type === "event" &&
    Array.isArray(access.extLinks) &&
    access.extLinks.length > 0);

/**
 * What every step says of the purchase, from the access shown and its
 * 1-tap purchase (`null` for none): one bought on the fan's behalf is
 * "one_tap", by its listing's id; an event sold only through its external
 * link is "external", whose funnel ends at the view; anything else is
 * Lissen's own. No seller is named. The app's access data has the artist's
 * id only, not their link page slug.
 */
export function purchaseFunnelProps(
  access: Access,
  purchase: Pick<Purchase, "listingId"> | null,
  funnelId: string,
): PurchaseFunnelProps {
  // A listing bought on the fan's behalf is named, never its seller
  // (LIS-1173): reports group by seller through the listing.
  const listingId = access.type === "music" ? null : (purchase?.listingId ?? null);
  return {
    funnel_id: funnelId,
    artist_slug: null,
    artist_id: access.artistId,
    access_id: access.id,
    item:
      access.type === "music"
        ? "music"
        : access.type === "merch"
          ? "merch"
          : "tickets",
    store: listingId
      ? "one_tap"
      : access.type === "event" && listedOf(access)
        ? "external"
        : "lissen",
    listing_id: listingId,
    surface: "app",
  };
}

/**
 * The Details screen shows an access: a new funnel for each access, its
 * product view recorded once, as soon as how it is sold is known (its
 * access read, with its purchase). The screen provides it to the purchase
 * sheets it opens.
 */
export function usePurchaseFunnelView(
  accessId: string,
  access: Access | undefined,
): PurchaseFunnel {
  // One funnel per access shown: another access is another purchase.
  const [funnel, setFunnel] = useState(() => ({ accessId, id: randomUuid() }));
  if (funnel.accessId !== accessId) setFunnel({ accessId, id: randomUuid() });
  const shown = access?.id === accessId ? access : undefined;
  const listed = shown !== undefined && listedOf(shown);
  const { purchase } = useAccessPurchase(listed ? accessId : null);
  const known = shown !== undefined && (!listed || purchase !== undefined);
  const props = useMemo(
    () =>
      known && shown
        ? purchaseFunnelProps(shown, purchase ?? null, funnel.id)
        : null,
    [known, shown, purchase, funnel.id],
  );
  const viewed = useRef<string | null>(null);
  useEffect(() => {
    if (!props || viewed.current === props.funnel_id) return;
    viewed.current = props.funnel_id;
    track("purchase_product_view", props);
  }, [props]);
  return useMemo(
    () => ({
      id: funnel.id,
      step: (step) => {
        if (props) recordStep(props, step);
      },
    }),
    [funnel.id, props],
  );
}

function recordStep(base: PurchaseFunnelProps, step: PurchaseFunnelStep) {
  switch (step.step) {
    case "sheet_open":
      return track("purchase_sheet_open", base);
    case "items_choose": {
      const props: Events["purchase_items_choose"] = {
        ...base,
        quantity: step.quantity,
        total_minor: step.totalMinor,
        currency: step.currency,
      };
      return track("purchase_items_choose", props);
    }
    case "contact_submit":
      return track("purchase_contact_submit", base);
    case "sheet_close":
      return track("purchase_sheet_close", {
        ...base,
        last_screen: step.lastScreen,
      });
  }
}
